import {
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  type Theme,
  useMediaQuery,
} from "@mui/material";
import { type FC, useEffect, useState } from "react";

import { tablePaginationProps } from "../TablePaginationActions";
import { EmptyTableCell, TableCellBody, TableCellHead } from "./TableCell";

const ROW_HEIGHT = 45.02;
const ROWS_PER_PAGE = 6;

const getEmptyRows = (rows: unknown[], page: number, rowsPerPage: number) =>
  rows.length === 0 ? rowsPerPage : rowsPerPage - Math.min(rowsPerPage, Math.abs(rows.length - page * rowsPerPage));

function getPaginatedRows<T extends unknown[]>(rows: T, page: number, rowsPerPage: number): T {
  // @ts-expect-error TS(2322) Something about instantiating with different subtype of type constraint on T
  return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
}

/** @deprecated We are phasing out `cc-ui` */
export interface BulkUploadPreviewProps {
  worksheet?: {
    headers?: unknown[][];
    rows?: unknown[][];
  };
}

/** @deprecated We are phasing out `cc-ui` */
export const BulkUploadPreview: FC<BulkUploadPreviewProps> = ({ worksheet }) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [emptyRows, setEmptyRows] = useState(0);
  const [paginatedRows, setPaginatedRows] = useState<unknown[][]>([]);

  useEffect(() => {
    // select smallest number - error occurs when worksheet updates and previously selected page is out of range
    const updatedPage = Math.min(page, Math.max((worksheet?.rows?.length ?? 0) - 1, 0));
    setPage(updatedPage);

    const updatedEmptyRows = getEmptyRows(worksheet?.rows ?? [], updatedPage, ROWS_PER_PAGE);
    setEmptyRows(updatedEmptyRows);

    const updatedPaginatedRows = getPaginatedRows(worksheet?.rows ?? [], updatedPage, ROWS_PER_PAGE);
    setPaginatedRows(updatedPaginatedRows);
  }, [page, worksheet]);

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage);

  return (
    <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
      <Table
        sx={{
          marginBottom: "1rem",
          tableLayout: "auto",
          width: "100%",
        }}
      >
        {worksheet?.headers && (
          <TableHead>
            {worksheet.headers.map((rows, outerIndex) => (
              <TableRow key={outerIndex}>
                {rows.map((value, index) =>
                  isMobile && index > 2 ? null : <TableCellHead key={`${value}-${index}`} value={value?.toString()} />,
                )}
              </TableRow>
            ))}
          </TableHead>
        )}
        <TableBody>
          {paginatedRows.map((rows, outerIndex) => (
            <TableRow key={outerIndex}>
              {rows.map((value, index) =>
                isMobile && index > 2 ? null : <TableCellBody key={`${value}-${index}`} value={value?.toString()} />,
              )}
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: ROW_HEIGHT * emptyRows }}>
              <EmptyTableCell />
            </TableRow>
          )}
        </TableBody>
        {worksheet?.rows && (
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={Number.MAX_SAFE_INTEGER}
                count={worksheet.rows.length}
                onPageChange={handleChangePage}
                page={page}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[]}
                {...tablePaginationProps}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};
